<template>
    <div class="scope-calendar scope-datepicker__wrapper">
        <div v-if="label" class="scope-datepicker__label">
            {{ label }}
        </div>
        <div
            :class="[
                'scope-datepicker__container',
                { '-full-width': fullWidth }
            ]"
        >
            <VueDatePicker
                ref="vueDatePicker"
                :style="{ width }"
                :class="[
                    'scope-datepicker',
                    `-${size}`,
                    {
                        '-invalid': invalid,
                        '-range': range,
                        '-multi': multiCalendars
                    }
                ]"
                v-model="modelValue"
                :max-date="maxDate"
                :min-date="minDate"
                auto-apply
                month-name-format="long"
                :multi-calendars="multiCalendars"
                :enable-time-picker="false"
                :range="range"
                :placeholder="placeholder"
                :prevent-min-max-navigation="Boolean(maxDate || minDate)"
                :inline="inline"
                :clearable="clearable"
                :format="computedFormat"
                @open="open"
                @closed="close"
                @cleared="emit('cleared')"
                @update:model-value="emit('updated')"
            >
                <template #calendar-icon>
                    <slot name="calendar-icon"></slot>
                </template>
                <template #arrow-left>
                    <Icon icon="ChevronLeft" />
                </template>
                <template #arrow-right>
                    <Icon icon="ChevronRight" />
                </template>
            </VueDatePicker>
            <div
                v-if="!inline"
                :class="['scope-datepicker__icon-wrapper', `-${size}`]"
            >
                <Icon
                    v-if="!modelValue"
                    :class="[
                        'scope-datepicker__switcher',
                        { '-active': opened }
                    ]"
                    icon="ChevronDown"
                />
            </div>
        </div>
        <div
            v-if="invalid && errorMessage"
            class="scope-datepicker__error"
        >
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {
    defineModel,
    defineProps,
    computed,
    nextTick,
    onMounted,
    ref
} from 'vue';
import Icon from './Icon.vue';

const emit = defineEmits(['open', 'closed', 'updated', 'cleared']);

const vueDatePicker = ref(null);

const props = defineProps({
    invalid: Boolean,
    errorMessage: String,
    openOnMounted: Boolean,
    range: Boolean,
    width: String,
    inline: Boolean,
    format: [Function, String],
    dateFormat: {
        type: String,
        default: 'dd/MM/yyyy'
    },
    fullWidth: Boolean,
    clearable: {
        type: Boolean,
        default: true
    },
    label: String,
    multiCalendars: Boolean | Object,
    size: {
        type: String,
        default: 'md'
    },
    maxDate: {
        type: Date,
        default: null
    },
    minDate: {
        type: Date,
        default: null
    },
    placeholder: {
        type: String,
        default: 'Select Date'
    }
});

const modelValue = defineModel();

const computedFormat = computed(() => props.format ?? props.dateFormat);

const opened = ref(false);

const open = () => {
    emit('open');
    opened.value = true;
    nextTick(() => {
        if (
            props.multiCalendars &&
            (props.maxDate || props.minDate) &&
            !modelValue.value
        ) {
            const currentDate = new Date();
            vueDatePicker.value.setMonthYear(
                currentDate.getFullYear(),
                currentDate.getMonth() - 1
            );
        }
    });
};
const close = () => {
    emit('closed');
    opened.value = false;
};

onMounted(() => {
    if (props.openOnMounted) {
        vueDatePicker.value.openMenu();
    }
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_main.scss";

.scope-datepicker {
  border: none;

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__container {
    position: relative;
    width: fit-content;

    &.-full-width {
      width: 100%;

      :deep(.scope-datepicker) {
        width: 100%
      }

    }
  }

  &__icon-wrapper {
    position: absolute;
    width: 16px;
    height: 16px;
    top: 50%;
    transform: translateY(-50%);
    right: 8px;
    display: flex;
    align-items: center;
    pointer-events: none;
    color: $text-mid-emphasis;

    &.-sm {
      top: 9px;
    }

    &.-lg {
      top: 16px
    }
  }

  &__switcher {
    transition-duration: 0.4s;

    &.-active {
      transform: rotate(180deg);
    }
  }

  &__label {
    color: $text-high-emphasis;
    @include fz-14;
    font-weight: 500;
  }

  &__error {
    @include fz-12;
    font-weight: 500;
    color: $border-error;
  }
}

:deep(.scope-datepicker) {
  --dp-cell-size: 40px;
  --dp-cell-padding: 8px;
  --dp-menu-padding: 16px;
  --dp-row-margin: : 0;
  width: fit-content;

  .dp__calendar {
    display: flex;
    flex-direction: column;
    gap: 0.5px;
  }

  .dp__input {
    padding: 4px 36px;
    @include fz-14;
    border-radius: 10px;

    &::placeholder {
      @include fz-14;
      font-weight: 500;
      color: $text-high-emphasis;
    }
  }

  .dp--menu-wrapper {
    background: $card-background;
    box-shadow: 0px 2px 22px 0px rgba(209, 209, 209, 0.40);
    border-radius: 16px;
  }

  .dp__btn {
    padding: 0;
  }

  .dp__menu {
    border-radius: 16px;
  }

  .dp__arrow_top {
    display: none;
  }

  .dp__calendar_header_separator {
    display: none;
  }

  .dp__flex_display {
    gap: 32px;
  }
  .dp__menu_inner {
    background: $card-background;
    border-radius: 16px;
  }
  .dp__menu_inner>div {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .dp__calendar_header_item,
  .dp__cell_inner {
    font-family: 'Geist', sans-serif;
    font-optical-sizing: auto;
    @include fz-14;
    font-weight: 500;
  }

  .dp__cell_inner {
    border-radius: 12px;
  }

  .dp__calendar_header_item {
    color: $text-mid-emphasis;
  }

  .dp__calendar_item:has(> .dp__cell_disabled) {
    pointer-events: none;
  }

  .dp__calendar_item:has(> .dp__today:hover),
  .dp__calendar_item:has(> .dp__range_start),
  .dp__calendar_item:has(> .dp__range_end),
  .dp__calendar_item:has(> .dp__range_start:hover),
  .dp__calendar_item:has(> .dp__range_end:hover),
  .dp__calendar_item:has(> .dp__today),
  .dp__calendar_item:has(> .dp__active_date),
  .dp__calendar_item:has(> .dp__active_date:hover) {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
    padding: 4px;
    border-radius: 12px;
  }

  .dp__today {
    border: unset;
  }

  .dp__calendar_item:has(> .dp__today.dp__range_between),
  .dp__calendar_item:has(> .dp__today.dp__range_between:hover) {
    border-radius: 0;
    padding: 0;
  }

  .dp__calendar_item:has(.dp__range_start),
  .dp__calendar_item:has(.dp__range_start:hover) {
    border-radius: 12px 0 0 12px;
  }

  .dp__calendar_item:has(.dp__range_end),
  .dp__calendar_item:has(.dp__range_end:hover) {
    border-radius: 0 12px 12px 0;
  }

  .dp__active_date,
  .dp__range_start,
  .dp__range_end,
  .dp__active_date:hover,
  .dp__today:hover,
  .dp-today {
    height: 32px;
    width: 32px;
    padding: 8px;
    border-radius: 8px;
  }

  .dp__range_between,
  .dp__range_between:hover {
    border-radius: unset;
    border: 0;
  }

  .dp__month_year_wrap {
    justify-content: space-between;
    align-items: center;
    min-height: 24px;

    .dp--arrow-btn-nav {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border-radius: 0;
      border: 0;
      background: transparent;

      &:has(> .dp__inner_nav_disabled) {
        visibility: hidden;
        pointer-events: none;
      }
      &:hover {
        background-color: transparent !important;

        .dp__inner_nav {
          color: $icons-high-emphasis;
        }
      }

      .dp__inner_nav {
        color: $icons-mid-emphasis;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;

        &:hover {
          background-color: transparent !important;
        }
      }
    }

    .dp__month_year_wrap {
      width: unset;
      gap: 4px;
      align-items: center;
      min-height: 24px;

      .dp__month_year_select {
        color: $text-high-emphasis;
        font-family: 'Geist', sans-serif;
        font-optical-sizing: auto;
        @include fz-14;
        font-weight: 500;
        background-color: transparent;
        width: fit-content;
        pointer-events: none;
        padding: 0;
        margin: 0;
        min-height: 20px;
        height: 20px;
        line-height: 20px;
        display: inline-flex;
        align-items: center;
      }
    }
  }
}

:deep(.scope-datepicker.-lg) {
  .dp__input {
    height: 48px;
  }
}

:deep(.scope-datepicker.-md) {
  .dp__input {
    height: 36px;
  }
}

:deep(.scope-datepicker.-sm) {
  .dp__input {
    height: 32px;
  }
}


:deep(.scope-datepicker.-range) {

  .dp__calendar_item:hover:has(> .dp--past),
  .dp__calendar_item:hover:has(> .dp--future),
  .dp__calendar_item:hover:has(> .dp--today) {
    border-radius: 0;

    display: flex;
    justify-content: center;
    align-items: center;

    height: 40px;
    width: 40px;
    padding: 4px;

    .dp--past,
    .dp--future,
    .dp--today {

      height: 32px;
      width: 32px;
      padding: 8px;
      border-radius: 8px;
    }
  }
}

:deep(.scope-datepicker.-multi) {
  .dp__month_year_wrap {
    justify-content:  center;

    .dp__month_year_wrap {
      width: 100%;
    }
  }
}


.scope-calendar {
    :deep(.scope-datepicker.-range) {

      .dp__calendar_item:hover:has(> .dp--past),
      .dp__calendar_item:hover:has(> .dp--future),
      .dp__calendar_item:hover:has(> .dp--today) {
        /* background-color: $card-primary-background-light; */

        .dp--past,
        .dp--future,
        .dp--today {
          background-color: $primary-accent;
          color: $text-light;
        }
      }
    }
    :deep(.scope-datepicker) {

      .dp__input {
        border: 1px solid $border;
        background: $card-background;
        color: $text-high-emphasis;
      }

      &.-invalid .dp__input {
        border-color: $border-error;
      }

      .dp--menu-wrapper {
        @include shadow-medium;
      }

      .dp__cell_inner {
        color: $text-high-emphasis;

        &:hover {
          background-color: $card-neutral-background;
        }
      }
      .dp__arrow_bottom {
        display: none;
      }
      .dp__cell_offset {
        color: $text-mid-emphasis;
      }

      .dp__calendar_header_item {
        color: $text-mid-emphasis;
      }

      .dp__cell_disabled {
        color: $text-mid-emphasis;
      }

      .dp__today {
        border: unset;
        color: $primary-accent;

        &:hover {
          background: $card-primary-background-light;
          border: 1px solid $primary-accent;
        }
      }

      .dp__calendar_item:has(> .dp__today:hover) {
        background: $card-primary-background-light;
      }

      .dp__calendar_item:has(> .dp__range_start),
      .dp__calendar_item:has(> .dp__range_end),
      .dp__calendar_item:has(> .dp__range_start:hover),
      .dp__calendar_item:has(> .dp__range_end:hover),
      .dp__calendar_item:has(> .dp__active_date),
      .dp__calendar_item:has(> .dp__active_date:hover) {
        background: $card-primary-background-light;
      }

      .dp__calendar_item:has(> .dp__today.dp__range_between),
      .dp__calendar_item:has(> .dp__today.dp__range_between:hover) {
        background-color: $card-primary-background-light;
      }

      .dp__range_start,
      .dp__range_end,
      .dp__range_start:hover,
      .dp__range_end:hover,
      .dp__active_date:hover,
      .dp__active_date {
        background-color: $primary-accent;
        color: $text-light;
      }

      .dp__range_between,
      .dp__range_between:hover {
        background-color: $card-primary-background-light;
        border-radius: unset;
        border: 0;
      }

      .dp__month_year_wrap {

      .dp--arrow-btn-nav {
          border: 0;
          border-radius: 0;
          background: transparent;

          .dp__inner_nav {
            color: $icons-mid-emphasis;

            &_disabled {
              background: $app-background !important;
            }
          }

      &:hover {
        background-color: transparent !important;

        .dp__inner_nav {
          color: $icons-high-emphasis;
        }
      }

      .dp__inner_nav {
        svg {
          width: 20px;
          height: 20px;
        }

        &:hover {
          background-color: transparent !important;
        }
      }
    }

        .dp__month_year_wrap {
          .dp__month_year_select {
            color: $text-high-emphasis;
          }
        }
      }
    }
}

body[theme="dark"] {
  .scope-datepicker__label {
    color: $text-high-emphasis;
  }
  .scope-calendar {
    :deep(.scope-datepicker) {
      .dp__menu {
        border: unset;
        background-color: $app-background;
        border: 1px solid $border-secondary;
      }
    }
  }
}
</style>
